import { Button, Chip, Stack } from '@mui/material';
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { palette, spaces, spacing, typography } from '../../../assets/styles/themes/tokens';
import {
	inclusionAdapted,
	pageContentTypes,
	type searchFilterKeys,
	teachingFormats,
	teachingMethods,
} from '../../../constants';
import { selectDocumentsPublishers } from '../../../reducers/publishers.selectors';
import { selectSchoolTypes } from '../../../reducers/schoolTypes.selectors';
import type { SearchFilters } from '../../../types/search';

type ActiveFilterKey = (typeof searchFilterKeys)[number];

const CHIP_HEIGHT = 30;
const CHIP_GAP = 2 * spacing; // spaces.xs = 8px
const MAX_VISIBLE_ROWS = 3;
const MAX_HEIGHT = MAX_VISIBLE_ROWS * CHIP_HEIGHT + (MAX_VISIBLE_ROWS - 1) * CHIP_GAP;

interface ActiveFilterChipsProps {
	requestFilters: SearchFilters;
	filterKeys: ActiveFilterKey[];
	onRemoveFilter: (key: ActiveFilterKey, value: string) => void;
	onResetAll: () => void;
}

const ActiveFilterChips: React.FC<ActiveFilterChipsProps> = ({
	requestFilters,
	filterKeys,
	onRemoveFilter,
	onResetAll,
}) => {
	const { t } = useTranslation();
	const publishers = useSelector(selectDocumentsPublishers);
	const schoolTypes = useSelector(selectSchoolTypes);
	// biome-ignore lint/correctness/useExhaustiveDependencies: pre-Biome hook deps kept as-is; changing the deps list alters effect timing
	const getLabel = useCallback(
		(key: ActiveFilterKey, value: string): string => {
			switch (key) {
				case 'publisher': {
					const publisher = publishers.find((p) => p.id === value);
					return publisher?.textValue ?? value;
				}
				case 'schoolType': {
					const schoolType = schoolTypes.find((st) => st.searchId === value);
					return schoolType?.name ?? value;
				}
				case 'teachingFormat':
					return t(teachingFormats.find((f) => f.key === value)?.label ?? value);
				case 'teachingMethod':
					return t(teachingMethods.find((m) => m.key === value)?.label ?? value);
				case 'pageContentType':
					return t(pageContentTypes.find((ct) => ct.key === value)?.label ?? value);
				case 'inclusionAdapted':
					return t(inclusionAdapted[0]?.label ?? value);
				case 'classYear':
				case 'subject':
				default:
					return value;
			}
		},
		[publishers, schoolTypes]
	);

	const chips = useMemo(() => {
		const result: { key: ActiveFilterKey; value: string; label: string }[] = [];
		for (const key of filterKeys) {
			const values = requestFilters[key];
			if (Array.isArray(values)) {
				for (const value of values) {
					result.push({ key, value, label: getLabel(key, value) });
				}
			} else if (typeof values === 'boolean' && values) {
				result.push({ key, value: 'true', label: getLabel(key, 'true') });
			}
		}
		return result;
	}, [requestFilters, filterKeys, getLabel]);

	const containerRef = useRef<HTMLDivElement>(null);
	const [isOverflowing, setIsOverflowing] = useState(false);
	const prevChipsLengthRef = useRef(chips.length);

	// Reset to wrap mode when chips change so we can re-measure
	if (prevChipsLengthRef.current !== chips.length) {
		prevChipsLengthRef.current = chips.length;
		setIsOverflowing(false);
	}

	// Measure in wrap mode (isOverflowing=false) before browser paints
	// biome-ignore lint/correctness/useExhaustiveDependencies: pre-Biome hook deps kept as-is; changing the deps list alters effect timing
	useLayoutEffect(() => {
		const el = containerRef.current;
		if (!el || isOverflowing) return;

		if (el.scrollHeight > MAX_HEIGHT) {
			setIsOverflowing(true);
		}
	}, [isOverflowing, chips.length]);

	// Re-measure on window resize, throttled via requestAnimationFrame
	useEffect(() => {
		let rafId: number | null = null;
		const handleResize = () => {
			if (rafId !== null) return;
			rafId = requestAnimationFrame(() => {
				setIsOverflowing(false);
				rafId = null;
			});
		};
		window.addEventListener('resize', handleResize);
		return () => {
			window.removeEventListener('resize', handleResize);
			if (rafId !== null) cancelAnimationFrame(rafId);
		};
	}, []);

	if (chips.length === 0) return null;

	return (
		<Stack
			ref={containerRef}
			direction="row"
			sx={{
				gap: spaces.xs,
				marginTop: spaces.xs,
				...(isOverflowing
					? { flexWrap: 'nowrap', overflowX: 'auto', paddingBottom: spaces.xs }
					: { flexWrap: 'wrap', maxHeight: MAX_HEIGHT }),
			}}
			data-testid="active-filter-chips"
		>
			{chips.map((chip) => (
				<Chip
					aria-label={`${chip.label} filter`}
					data-testid={`active-filter-chip-${chip.key}`}
					key={`${chip.key}-${chip.value}`}
					label={chip.label}
					onDelete={() => onRemoveFilter(chip.key, chip.value)}
					sx={{
						height: CHIP_HEIGHT,
						flexShrink: 0,
						backgroundColor: palette.action.selected,
						color: palette.text.primary,
						'& .MuiChip-label': {
							paddingX: spaces.xs,
							paddingY: spaces.xxxs,
							...typography.chip.label,
						},
						'& .MuiChip-deleteIcon': {
							width: 16,
							height: 16,
							opacity: 0.7,
							marginRight: 0,
							color: palette.components.chip.defaultCloseFill,
						},
					}}
				/>
			))}
			<Button
				sx={{
					color: palette.text.primary,
					...typography.button.small,
					paddingX: spaces.xs,
					paddingY: spaces.xxs,
					whiteSpace: 'nowrap',
					flexShrink: 0,
				}}
				variant="text"
				data-testid="active-filter-chips-reset-all"
				onClick={onResetAll}
			>
				{t('Reset all')}
			</Button>
		</Stack>
	);
};

export default ActiveFilterChips;
